// src/helpers/rutaOrdenPedido.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.mensaje ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const toNumberMinOne = (v, def = 1) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return def;
  return n <= 0 ? def : n;
};

const unwrapPedidosDisponibles = (data) => {
  if (!data) {
    return {
      pedidos: [],
      resumen: {},
    };
  }

  return {
    pedidos: Array.isArray(data?.pedidos) ? data.pedidos : [],
    resumen: data?.resumen || {},
  };
};

const unwrapOrdenPedido = (data) => {
  if (!data) return null;
  if (data?.ordenPedido) return data.ordenPedido;
  if (data?.data) return data.data;
  return data;
};

const unwrapOrdenesPedido = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.ordenes)) return data.ordenes;
  if (Array.isArray(data?.ordenesPedido)) return data.ordenesPedido;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

export const normalizarItemOrdenPedidoPayload = (item = {}) => {
  const src = item || {};

  return {
    pedidoKey: clean(src.pedidoKey),
    cantidadOrdenada: toNumberMinOne(
      src.cantidadOrdenada ?? src.cantidad,
      1
    ),
  };
};

export const normalizarOrdenPedidoPayload = (payload = {}) => {
  const src = payload || {};

  return {
    fecha: src.fecha || undefined,
    ordenarPor: clean(src.ordenarPor || "PRODUCTO").toUpperCase(),
    observacion: clean(src.observacion || src.observaciones).toUpperCase(),
    observaciones: clean(src.observaciones || src.observacion).toUpperCase(),

    items: Array.isArray(src.items)
      ? src.items
          .map((item) => normalizarItemOrdenPedidoPayload(item))
          .filter((item) => item.pedidoKey)
      : [],
  };
};

/* --------------------------- ORDEN DE PEDIDO ----------------------------- */

/**
 * Trae los pedidos disponibles para armar una orden.
 *
 * Alimenta la tabla izquierda.
 */
export async function getPedidosDisponiblesOrdenPedido({
  desde = "",
  hasta = "",
  producto = "",
  provincia = "",
  municipio = "",
  zonaLogistica = "",
  buscar = "",
  ordenarPor = "PRODUCTO",
  estadoPago = "",
  signal,
} = {}) {
  const params = {};

  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;
  if (producto) params.producto = producto;
  if (provincia) params.provincia = provincia;
  if (municipio) params.municipio = municipio;
  if (zonaLogistica) params.zonaLogistica = zonaLogistica;
  if (buscar) params.buscar = String(buscar || "").trim();
  if (ordenarPor) params.ordenarPor = ordenarPor;
  if (estadoPago) params.estadoPago = estadoPago;

  const data = await req("GET", p("ordenpedido/disponibles"), {
    params,
    signal,
  });

  return unwrapPedidosDisponibles(data);
}

/**
 * Genera la orden con los pedidos seleccionados.
 *
 * El front debe mandar items con pedidoKey.
 */
export async function generarOrdenPedido(payload = {}, { signal } = {}) {
  const data = await req("POST", p("ordenpedido"), {
    data: normalizarOrdenPedidoPayload(payload),
    signal,
  });

  return unwrapOrdenPedido(data);
}

/**
 * Lista órdenes ya generadas.
 */
export async function getOrdenesPedido({
  estado = "",
  desde = "",
  hasta = "",
  buscar = "",
  signal,
} = {}) {
  const params = {};

  if (estado) params.estado = estado;
  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;
  if (buscar) params.buscar = String(buscar || "").trim();

  const data = await req("GET", p("ordenpedido"), {
    params,
    signal,
  });

  const ordenes = unwrapOrdenesPedido(data);

  return {
    ordenes,
    total: toNumber(data?.total, ordenes.length),
  };
}

/**
 * Detalle de una orden generada.
 */
export async function getOrdenPedidoById(id, { signal } = {}) {
  if (!id) {
    throw new Error("ID de orden de pedido requerido.");
  }

  const data = await req("GET", p(`ordenpedido/${id}`), {
    signal,
  });

  return unwrapOrdenPedido(data);
}

/**
 * Anula una orden y libera los pedidos para volver a organizarlos.
 */
export async function anularOrdenPedido(id, motivo = "", { signal } = {}) {
  if (!id) {
    throw new Error("ID de orden de pedido requerido.");
  }

  const data = await req("PUT", p(`ordenpedido/${id}/anular`), {
    data: {
      motivo: clean(motivo).toUpperCase(),
    },
    signal,
  });

  return unwrapOrdenPedido(data);
}